import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "../../lib/utils"

const buttonVariants = cva(
  "inline-flex items-center justify-center whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50",
  {
    variants: {
      variant: {
        default: "bg-primary text-white hover:bg-primary/90",
        destructive:
          "bg-destructive text-destructive-foreground hover:bg-destructive/90",
        outline:
          "border border-input bg-background hover:bg-accent hover:text-foreground",
        secondary:
          "bg-secondary text-muted-foreground hover:bg-secondary/80",
        ghost: "hover:bg-accent hover:text-accent-foreground",
        link: "text-foreground underline-offset-4 hover:underline",
        clean: "bg-transparent hover:bg-transparent hover:text-foreground",
      },
      size: {
        default: "h-10 px-4 py-2",
        sm: "h-9 rounded-md px-3",
        lg: "h-11 rounded-md px-8",
        icon: "h-10 w-10",
        xs: "h-6 rounded-lg px-2 text-xs",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
  VariantProps<typeof buttonVariants> {
  asChild?: boolean
  /**
   * Opt into a two-click confirmation flow. The first click "arms" the button:
   * over 2s the label fades out and `confirmLabel` fades in, and the button is
   * un-clickable throughout. Only the second click (once armed) fires `onClick`.
   * If not confirmed within 4s of arming, it reverts to the original label.
   * `asChild` is ignored while `confirm` is set.
   */
  confirm?: boolean
  /** Content shown once the button is armed. Only used when `confirm` is set. */
  confirmLabel?: React.ReactNode
}

// Timings for the confirm flow (ms). The 1000 fade-out + 1000 fade-in is a
// deliberate friction gate; the button is disabled for that whole 2s window.
const CONFIRM_FADE_MS = 1000
const CONFIRM_REVERT_MS = 300
const CONFIRM_AUTO_REVERT_MS = 4000

// idle           → original label, clickable (first click arms)
// fadingOut      → label fades out (0–1000ms), disabled
// fadingIn       → confirmLabel fades in (1000–2000ms), disabled
// armed          → confirmLabel shown, clickable (second click confirms)
// Under reduced motion, fadingOut is skipped: the swap is instant but the 2s
// non-clickable gate is preserved via a single fadingIn hold.
type ConfirmPhase = "idle" | "fadingOut" | "fadingIn" | "armed"

const usePrefersReducedMotion = (): boolean => {
  const [prefers, setPrefers] = React.useState(false)
  React.useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)")
    setPrefers(mq.matches)
    const onChange = (e: MediaQueryListEvent) => setPrefers(e.matches)
    mq.addEventListener("change", onChange)
    return () => mq.removeEventListener("change", onChange)
  }, [])
  return prefers
}

const ConfirmButton = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, type = "button", confirm: _confirm, confirmLabel, children, onClick, disabled, ...props }, ref) => {
    const [phase, setPhase] = React.useState<ConfirmPhase>("idle")
    // Duration applied to the current opacity transition — slow while arming,
    // quick when reverting back to idle.
    const [fadeMs, setFadeMs] = React.useState(CONFIRM_FADE_MS)
    const reducedMotion = usePrefersReducedMotion()
    const timers = React.useRef<ReturnType<typeof setTimeout>[]>([])

    const clearTimers = React.useCallback(() => {
      timers.current.forEach(clearTimeout)
      timers.current = []
    }, [])

    React.useEffect(() => clearTimers, [clearTimers])

    const revertToIdle = React.useCallback(() => {
      clearTimers()
      setFadeMs(reducedMotion ? 0 : CONFIRM_REVERT_MS)
      setPhase("idle")
    }, [clearTimers, reducedMotion])

    const handleClick = React.useCallback(
      (event: React.MouseEvent<HTMLButtonElement>) => {
        if (phase === "idle") {
          // First click arms the button; it does NOT fire the caller's handler.
          event.preventDefault()
          clearTimers()
          setFadeMs(reducedMotion ? 0 : CONFIRM_FADE_MS)
          const armWhenReady = () => {
            setPhase("armed")
            timers.current.push(setTimeout(revertToIdle, CONFIRM_AUTO_REVERT_MS))
          }
          if (reducedMotion) {
            // Instant swap, but keep the full 2s non-clickable gate.
            setPhase("fadingIn")
            timers.current.push(setTimeout(armWhenReady, CONFIRM_FADE_MS * 2))
          } else {
            setPhase("fadingOut")
            timers.current.push(
              setTimeout(() => setPhase("fadingIn"), CONFIRM_FADE_MS)
            )
            timers.current.push(setTimeout(armWhenReady, CONFIRM_FADE_MS * 2))
          }
          return
        }
        if (phase === "armed") {
          revertToIdle()
          onClick?.(event)
        }
        // While fading the button is disabled, so no click reaches here.
      },
      [phase, reducedMotion, clearTimers, revertToIdle, onClick]
    )

    const isFading = phase === "fadingOut" || phase === "fadingIn"
    // Label is only fully shown in idle; it fades out the moment arming starts.
    const labelVisible = phase === "idle"
    const confirmVisible = phase === "fadingIn" || phase === "armed"
    const transitionClass = "transition-opacity ease-in-out"
    const fadeStyle: React.CSSProperties = { transitionDuration: `${fadeMs}ms` }

    return (
      <button
        className={cn(buttonVariants({ variant, size }), className)}
        ref={ref}
        type={type}
        onClick={handleClick}
        disabled={disabled || isFading}
        aria-disabled={isFading || undefined}
        {...props}
      >
        {/* Both labels share one grid cell so the button reserves the wider of the
            two and never jumps width mid-animation. */}
        <span className="grid" aria-live="polite">
          <span
            className={cn("col-start-1 row-start-1", transitionClass)}
            style={{ ...fadeStyle, opacity: labelVisible ? 1 : 0 }}
            aria-hidden={!labelVisible || undefined}
          >
            {children}
          </span>
          <span
            className={cn("col-start-1 row-start-1", transitionClass)}
            style={{ ...fadeStyle, opacity: confirmVisible ? 1 : 0 }}
            aria-hidden={!confirmVisible || undefined}
          >
            {confirmLabel}
          </span>
        </span>
      </button>
    )
  }
)
ConfirmButton.displayName = "ConfirmButton"

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, type = "button", confirm = false, confirmLabel, ...props }, ref) => {
    if (confirm) {
      return (
        <ConfirmButton
          ref={ref}
          className={className}
          variant={variant}
          size={size}
          type={type}
          confirm
          confirmLabel={confirmLabel}
          {...props}
        />
      )
    }
    const Comp = asChild ? Slot : "button"
    return (
      <Comp
        className={cn(buttonVariants({ variant, size }), className)}
        ref={ref}
        type={type}
        {...props}
      />
    )
  }
)
Button.displayName = "Button"

export { Button, buttonVariants }
